import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Star } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PlansPanel() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/loueur/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/loueur/subscribe", { plan, billing, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setLoading(""); }
  };

  const fmt = (v) => (v % 1 === 0 ? v : v.toFixed(2));

  return (
    <div data-testid="plans-panel">
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 bg-white rounded-2xl border border-border p-5">
          <p className="text-xs uppercase font-bold text-muted2 tracking-wide">{t("plans.current")}</p>
          <p data-testid="current-plan-name" className="font-heading font-extrabold text-2xl text-canard mt-1">{sub?.plan_name || "—"}</p>
        </div>
        <div className="flex-1 bg-gradient-to-br from-canard to-emeraude-dark text-white rounded-2xl p-5">
          <p className="text-xs uppercase font-bold text-white/80 tracking-wide flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {t("plans.points")}</p>
          <p data-testid="loyalty-points" className="font-heading font-extrabold text-2xl mt-1">{sub?.points ?? 0}</p>
          <p className="text-[11px] text-white/70 mt-1">{t("plans.pointsInfo")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-6">
        <button data-testid="billing-monthly" onClick={() => setBilling("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>{t("plans.monthly")}</button>
        <button data-testid="billing-yearly" onClick={() => setBilling("yearly")} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${billing === "yearly" ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>{t("plans.yearly")} <span className="text-emeraude-dark bg-emeraude/15 rounded-full px-1.5 ml-1 text-xs">{t("plans.save15")}</span></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const isCurrent = (sub?.plan || "free") === p.key;
          const price = p.key === "free" ? 0 : (billing === "yearly" ? p.price_yearly : p.price_monthly);
          const highlight = p.key === "croissance";
          const pd = t(`planDefs.${p.key}`, { returnObjects: true, defaultValue: null });
          const name = pd?.name || p.name;
          const feats = Array.isArray(pd?.features) ? pd.features : p.features;
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`} className={`relative rounded-2xl border p-5 flex flex-col bg-white ${highlight ? "border-canard ring-2 ring-canard/25" : "border-border"}`}>
              {highlight && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-canard text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full tracking-wide">POPULAIRE</span>}
              <p className="font-heading font-extrabold text-lg text-ink">{name}</p>
              <p className="mt-2">
                <span className="font-heading font-extrabold text-3xl text-canard">{p.key === "free" ? t("plans.free") : `${fmt(price)}€`}</span>
                {p.key !== "free" && <span className="text-xs text-muted2">{billing === "yearly" ? t("plans.perYear") : t("plans.perMonth")}</span>}
              </p>
              <ul className="mt-4 space-y-2 flex-1">
                {feats.map((f) => (<li key={f} className="flex gap-2 text-sm text-ink"><Check className="w-4 h-4 text-emeraude shrink-0 mt-0.5" /><span>{f}</span></li>))}
              </ul>
              {isCurrent ? (
                <div data-testid={`plan-current-${p.key}`} className="mt-4 text-center text-sm font-bold text-emeraude-dark bg-emeraude/10 rounded-full py-2">{t("plans.currentBadge")}</div>
              ) : p.key === "free" ? null : (
                <Button data-testid={`subscribe-${p.key}`} disabled={loading === p.key} onClick={() => subscribe(p.key)} className="mt-4 rounded-full bg-canard hover:bg-canard-dark font-bold">{loading === p.key ? t("plans.processing") : t("plans.subscribe")}</Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
